import * as Dialog from "@radix-ui/react-dialog";
import { Icon } from "../Icon/Icon";
import { useTheme } from "../Theme/ThemeProvider";
import React, { useRef, useState, useEffect } from "react";
import { ThemeFontSlider } from "./ThemeFontSlider";
// import { ThemeAccentColors, ACCENT_LIST } from "./ThemeAccentColors";
import { getUserInitials } from "../../utils/userUtils";
import { useAccount } from "@azure/msal-react";

// User bubble color options
const USER_BUBBLE_COLORS = [
  // Default option
  { name: 'Default', value: 'default' },
  
  // Solid colors
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Gray', value: '#6b7280' },
  
  // Gradients
  { name: 'Ocean', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { name: 'Sunset', value: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)' },
  { name: 'Forest', value: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)' },
  { name: 'Fire', value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
  { name: 'Aurora', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' },
  { name: 'Galaxy', value: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)' },
  { name: 'Peachy', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
  { name: 'Lavender', value: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)' },
  { name: 'Mint', value: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)' },
  { name: 'Rose Gold', value: 'linear-gradient(135deg, #e91e63 0%, #f8bbd9 100%)' },
];

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(val, max));
}

// Cookie utilities for enterprise environments
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const date = new Date();
  date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
}

/** Theme dialog content - separated from Dialog.Root for reusability */
export function ThemeDialogContent() {
  const { mode, setMode, accent, isUsingSystemTheme } = useTheme();
  const account = useAccount();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  
  // User bubble color state with cookie fallback
  const [userBubbleColor, setUserBubbleColor] = useState(() => {
    return localStorage.getItem('user-bubble-color') || getCookie('user-bubble-color') || USER_BUBBLE_COLORS[0].value;
  });
  // Time format preference state (auto | 12h | 24h)
  const [timeFormatPref, setTimeFormatPref] = useState<string>(() => {
    return localStorage.getItem('timeFormatPref') || 'auto';
  });

  // Save user bubble color to both localStorage and cookie when it changes
  useEffect(() => {
    localStorage.setItem('user-bubble-color', userBubbleColor);
    setCookie('user-bubble-color', userBubbleColor);
  }, [userBubbleColor]);

  useEffect(() => {
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      setBoxSize({ width: rect.width, height: rect.height });
    }
  }, [mode, accent]);

  /** Mouse event handlers for dragging the dialog */
  function onMouseDown(e: React.MouseEvent) {
    dragging.current = true;
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  function onMouseMove(e: MouseEvent) {
    if (dragging.current && boxRef.current) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let newX = e.clientX - offset.current.x;
      let newY = e.clientY - offset.current.y;
      const minX = -(vw / 2) + boxSize.width / 2;
      const maxX = vw / 2 - boxSize.width / 2;
      const minY = -(vh / 2) + boxSize.height / 2;
      const maxY = vh / 2 - boxSize.height / 2;
      setPosition({
        x: clamp(newX, minX, maxX),
        y: clamp(newY, minY, maxY),
      });
    }
  }

  function onMouseUp() {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }

  // Use CSS variable for accent color
  const accentColorValue = 'var(--color-accent)';
  
  return (
    <Dialog.Portal>
      <Dialog.Content asChild>
        <div
          ref={boxRef}
          className="bg-[var(--color-secondary)] rounded-lg text-text p-6 flex flex-col items-center"
          style={{
            position: "fixed",
            left: `calc(50% + ${position.x}px)`,
            top: `calc(50% + ${position.y}px)`,
            transform: "translate(-50%, -50%)",
            zIndex: 50,
            boxShadow: `0 6px 18px 6px var(--color-secondary), 2px 4px 16px 4px rgba(0, 0, 0, 0.5)`
          }}
        >
          {/* Only the title is draggable */}
          <Dialog.Title
            className="mb-4 text-lg font-medium select-none cursor-move w-full text-center"
            onMouseDown={onMouseDown}
          >
            Customize Theme
            {isUsingSystemTheme && (
              <div className="text-xs font-normal opacity-70 mt-1">
                Following system theme
              </div>
            )}
          </Dialog.Title>
          {/* Light/Dark mode toggle */}
          <div className="flex gap-4 mb-6 items-center">
            <button
              className={`px-4 py-2 flex items-center justify-center ${
                mode === "light" ? "font-extrabold scale-110" : "opacity-70"
              }`}
              onClick={() => setMode("light")}
              aria-label="Light mode"
            >
              <Icon name="sun" width={24} height={24} strokeWidth={10} />
            </button>
            <div
              className="w-px h-6 mx-1 rounded"
              style={{
                background: accentColorValue,
                minWidth: "2px",
              }}
            />
            <button
              className={`px-4 py-2 flex items-center justify-center ${
                mode === "dark" ? "font-extrabold scale-110" : "opacity-50"
              }`}
              onClick={() => setMode("dark")}
              aria-label="Dark mode"
            >
              <Icon name="moon" width={24} height={24} />
            </button>
          </div>
          {/* Accent color choices */}
          {/* <ThemeAccentColors accent={accent} setAccent={setAccent} accentColors={ACCENT_LIST} /> */}
          {/* Font size choices */}
          <ThemeFontSlider />
          
          {/* User Bubble Color Section */}
          <div className="w-full mt-6">
            <h3 className="text-sm font-medium mb-3 text-center text-[var(--color-text)]">
              Your Chat Bubble Color
            </h3>
            
            {/* Preview bubble with user initials */}
            <div className="flex justify-center mb-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold shadow-lg"
                style={{ 
                  background: userBubbleColor === 'default' ? 'var(--color-accent)' : userBubbleColor,
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                {getUserInitials(account?.name)}
              </div>
            </div>
            
            {/* Color options grid */}
            <div className="grid grid-cols-6 gap-2 max-w-xs mx-auto">
              {USER_BUBBLE_COLORS.map((color, index) => (
                <button
                  key={index}
                  className={`w-8 h-8 rounded-full border-2 transition-all duration-200 hover:scale-110 flex items-center justify-center ${
                    userBubbleColor === color.value 
                      ? 'border-white shadow-lg scale-110' 
                      : 'border-gray-300 hover:border-white'
                  }`}
                  style={{ 
                    background: color.value === 'default' ? 'var(--color-accent)' : color.value 
                  }}
                  onClick={() => setUserBubbleColor(color.value)}
                  title={color.name}
                  aria-label={`Select ${color.name} color`}
                >
                  {color.value === 'default' && (
                    <span className="text-white text-xs font-bold">D</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Time Format Preference */}
          <div className="w-full mt-8">
            <h3 className="text-sm font-medium mb-3 text-center text-[var(--color-text)]">
              Time Format
            </h3>
            <div className="flex justify-center gap-3 flex-wrap">
              {[
                { key: 'auto', label: 'Auto' },
                { key: '12h', label: '12h' },
                { key: '24h', label: '24h' }
              ].map(opt => {
                const active = timeFormatPref === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => {
                      let val = opt.key;
                      setTimeFormatPref(val);
                      if (val === 'auto') {
                        localStorage.removeItem('timeFormatPref');
                      } else {
                        localStorage.setItem('timeFormatPref', val);
                      }
                      // Notify listeners in same tab immediately
                      window.dispatchEvent(new CustomEvent('timeFormatPrefOverride'));
                    }}
                    className={`px-3 py-1 rounded text-xs border transition-colors ${active ? 'bg-[var(--color-accent)] text-white border-[var(--color-accent)]' : 'bg-[var(--button-background-color)] text-[var(--color-text)] border-[var(--color-border)] hover:border-[var(--color-accent)]'}`}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] mt-2 text-center opacity-70 px-4 leading-snug">
              Auto chooses 12 or 24 hour based on your browser/locale. Override here if it doesn't match your system tray clock.
            </p>
          </div>
          
          <div className="flex gap-3 mt-6">
            {!isUsingSystemTheme && (
              <button
                className="px-3 py-1 rounded border border-[var(--color-accent-hover)] hover:bg-[var(--color-accent-hover)] hover:text-[var(--color-bg)] bg-[var(--button-background-color)] text-[var(--color-text)] transition text-xs"
                onClick={() => {
                  // Clear stored preferences to go back to system theme
                  localStorage.removeItem('theme-mode');
                  localStorage.removeItem('theme-accent');
                  localStorage.removeItem('theme-user-preference');
                  localStorage.removeItem('user-bubble-color');
                  localStorage.removeItem('timeFormatPref');
                  window.location.reload(); // Simple way to reset the theme provider
                }}
              >
                Reset to System Defaults
              </button>
            )}
            <Dialog.Close className="px-3 py-1 rounded border border-[var(--color-accent-hover)] hover:bg-[var(--color-accent-hover)] hover:text-[var(--color-bg)] bg-[var(--button-background-color)] text-[var(--color-text)] transition text-sm">
              Close
            </Dialog.Close>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );
}